import { cloneDeep as _cloneDeep, omitBy as _omitBy, isUndefined as _isUndefined } from 'lodash';
import { View, ViewAttachment } from 'common/types/view';
import { Attachment } from 'common/types/revision';
import { AssetMetadata, InnerMetadataObject } from '../../../types';

const coreAttachmentToAttachment = (coreAttachment: ViewAttachment): Attachment => {
  const identifier =
    'assetId' in coreAttachment
      ? { asset_id: coreAttachment.assetId, blob_id: null }
      : { asset_id: null, blob_id: coreAttachment.blobId };
  return {
    name: coreAttachment.name,
    filename: coreAttachment.filename,
    ...identifier,
  };
};

export const attachmentToCoreAttachment = (attachment: Attachment): ViewAttachment => {
  const identifier = attachment.asset_id ? { assetId: attachment.asset_id } : { blobId: attachment.blob_id! };
  return {
    name: attachment.name,
    filename: attachment.filename,
    ...identifier,
  };
};

export const viewToAssetMetadata = (view: View): AssetMetadata => {
  const innerMetadataObject: InnerMetadataObject = view.metadata;

  let attachments: Attachment[];

  if (innerMetadataObject.attachments) {
    attachments = innerMetadataObject.attachments.map((coreAttachment) =>
      coreAttachmentToAttachment(coreAttachment),
    );
  } else {
    attachments = [];
  }

  return {
    builtIn: {
      name: view.name,
      rowLabel: innerMetadataObject.rowLabel,
      rdfSubject: innerMetadataObject.rdfSubject,
      rdfClass: innerMetadataObject.rdfClass,
      tags: view.tags || null,
      resourceName: view.resourceName,
      attachments,
      license: {
        name: view.license?.name || '',
        termsLink: view.license?.termsLink || '',
        licenseId: view.licenseId || null,
      },
      attribution: {
        name: view.attribution || null,
        link: view.attributionLink || null,
      },
      description: view.description || null,
      category: view.category || null,
    },
    customFields: _cloneDeep(innerMetadataObject?.custom_fields),
    privateMetadata: {
      contactEmail: view.privateMetadata?.contactEmail || null,
      customFields: _cloneDeep(view.privateMetadata?.custom_fields) || {},
    },
    results: {
      blob: _cloneDeep(innerMetadataObject),
      privateBlob: _cloneDeep(view.privateMetadata),
    },
  };
};

export const assetMetadataToView = (view: View, assetMetadata: AssetMetadata): View => {
  const updatedTags = assetMetadata.builtIn.tags !== null ? assetMetadata.builtIn.tags : undefined;

  let updatedAttachments: ViewAttachment[] | undefined;

  if (assetMetadata.builtIn.attachments.length > 0) {
    updatedAttachments = assetMetadata.builtIn.attachments.map((attachment) =>
      attachmentToCoreAttachment(attachment),
    );
  } else {
    updatedAttachments = undefined;
  }

  const updatedLicenseId =
    assetMetadata.builtIn.license.licenseId !== null ? assetMetadata.builtIn.license.licenseId : undefined;
  const updatedLicenseName =
    assetMetadata.builtIn.license.name !== '' ? assetMetadata.builtIn.license.name : undefined;
  const updatedLicenseTermsLink =
    assetMetadata.builtIn.license.termsLink !== '' ? assetMetadata.builtIn.license.termsLink : undefined;

  const updatedLicense = updatedLicenseName
    ? {
        name: updatedLicenseName,
        termsLink: updatedLicenseTermsLink,
      }
    : undefined;

  const updatedAttribution = assetMetadata.builtIn.attribution.name;
  const updatedAttributionLink = assetMetadata.builtIn.attribution.link;
  const updatedDescription = assetMetadata.builtIn.description;
  const updatedCategory = assetMetadata.builtIn.category;

  const updatedMetadataBlob = _omitBy(
    {
      ...assetMetadata.results?.blob,
      rowLabel: assetMetadata.builtIn.rowLabel,
      rdfSubject: assetMetadata.builtIn.rdfSubject,
      rdfClass: assetMetadata.builtIn.rdfClass,
      attachments: updatedAttachments,
    },
    _isUndefined,
  );

  if (assetMetadata.customFields && Object.keys(assetMetadata.customFields).length > 0) {
    updatedMetadataBlob.custom_fields = assetMetadata.customFields;
  }

  let updatedPrivateMetadata = {
    ...assetMetadata.results?.privateBlob,
  };

  updatedPrivateMetadata.contactEmail =
    assetMetadata.privateMetadata.contactEmail !== null ? assetMetadata.privateMetadata.contactEmail : '';

  updatedPrivateMetadata.custom_fields =
    Object.keys(assetMetadata.privateMetadata.customFields).length > 0
      ? assetMetadata.privateMetadata.customFields
      : undefined;

  updatedPrivateMetadata = _omitBy(updatedPrivateMetadata, _isUndefined);

  if (Object.keys(updatedPrivateMetadata).length === 0) {
    updatedPrivateMetadata = undefined;
  }

  const viewChanges: Partial<View> = _omitBy(
    {
      name: assetMetadata.builtIn.name,
      metadata: updatedMetadataBlob,
      tags: updatedTags,
      resourceName: assetMetadata.builtIn.resourceName,
      licenseId: updatedLicenseId,
      license: updatedLicense,
      attribution: updatedAttribution,
      attributionLink: updatedAttributionLink,
      description: updatedDescription,
      category: updatedCategory,
      privateMetadata: updatedPrivateMetadata,
    } as Partial<View>,
    _isUndefined,
  );

  return {
    ...view,
    ...viewChanges,
  };
};
